import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { Suspense, useEffect, useState } from 'react';
import { useAuthStore } from './stores/authStore';
import api from './utils/api';
import { clearStuckOverlays } from './utils/onboardingStorage';
import Login from './pages/Login';
import SignUp from './pages/SignUp';
import ResetPassword from './pages/ResetPassword';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiePolicy from './pages/CookiePolicy';
import Support from './pages/Support';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import {
  PortalSelection,
  ProjectPortal,
  CompanyPortal,
  UserPortal,
  CompanyInductionSearchResult,
  CompanyInduction,
  HealthMedicalAssessmentIntro,
  HealthMedicalAssessment,
  SiteInductionIntro,
  SiteInduction,
  SystemInduction,
  Dashboard,
  Timesheets,
  Inductions,
  ExportDownload,
  Incidents,
  Deliveries,
  Inspections,
  Assets,
  Vehicles,
  Settings,
  Projects,
  CreateProject,
  EditProject,
  ProjectDashboard,
  GoodCalls,
  GoodCallsDashboard,
  CloseCallsDashboard,
  CloseCalls,
  RAMS,
  ToolboxTalks,
  TaskBriefing,
  GiveTaskBriefing,
  ViewTaskBriefingDocument,
  TakeTaskBriefingRegister,
  Requisitions,
  RequisitionForm,
  RequisitionReturnForm,
  AssetManagement,
  AssetManagementHome,
  PlantAndMachineryDailyCheck,
  EquipmentDailyCheck,
  AccessEquipmentDailyCheck,
  TemporaryWorksDailyCheck,
  GiveBriefing,
  ViewRAMSDocument,
  TakeRegister,
  GiveToolboxTalkBriefing,
  ViewToolboxTalkDocument,
  TakeToolboxTalkRegister,
  Employees,
  CompanyInductionsList,
  CompanyMaps,
  CompanyProjects,
  CompanyPortalDashboard,
  MyData,
  NotFound,
  Layout,
  ProjectDashboardLayout,
  CompanyPortalProjectLayout,
  CompanyPortalGuard
} from './lazyPages';

function RouteLoadingFallback() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <CircularProgress sx={{ color: '#115740' }} />
    </Box>
  );
}

function PrivateRoute({ children }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const hasHydrated = useAuthStore((s) => s._hasHydrated);
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  const [checkingSystemInduction, setCheckingSystemInduction] = useState(false);
  const [isSystemInductionComplete, setIsSystemInductionComplete] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const shouldCheckAdminInduction =
      isAuthenticated &&
      user?.role === 'admin';

    if (!shouldCheckAdminInduction) {
      setCheckingSystemInduction(false);
      setIsSystemInductionComplete(true);
      return;
    }

    const checkSystemInduction = async () => {
      setCheckingSystemInduction(true);
      try {
        const response = await api.get('/system-induction');
        const completed = response?.data?.data?.profile?.induction_completed === true;
        if (!cancelled) setIsSystemInductionComplete(completed);
      } catch {
        if (!cancelled) setIsSystemInductionComplete(false);
      } finally {
        if (!cancelled) setCheckingSystemInduction(false);
      }
    };

    checkSystemInduction();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.id, user?.role, location.pathname]);

  if (!hasHydrated) {
    return <RouteLoadingFallback />;
  }

  if (isAuthenticated && user?.role === 'admin' && checkingSystemInduction) {
    return <RouteLoadingFallback />;
  }

  if (
    isAuthenticated &&
    user?.role === 'admin' &&
    !isSystemInductionComplete &&
    location.pathname !== '/system-induction'
  ) {
    return <Navigate to="/system-induction" replace />;
  }

  return isAuthenticated ? (
    <>
      {children}
      <PWAInstallPrompt />
    </>
  ) : (
    <Navigate to="/login" state={{ from: location }} replace />
  );
}

function RouteOverlayCleanup() {
  const location = useLocation();
  useEffect(() => {
    clearStuckOverlays();
  }, [location.pathname]);
  return null;
}

function App() {
  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <RouteOverlayCleanup />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/support" element={<Support />} />

        <Route
          path="/exports/download"
          element={
            <PrivateRoute>
              <ExportDownload />
            </PrivateRoute>
          }
        />

        {/* Portal Selection - No Layout */}
        <Route
          path="/portal-selection"
          element={
            <PrivateRoute>
              <PortalSelection />
            </PrivateRoute>
          }
        />

        {/* Portal Pages - No Layout */}
        <Route
          path="/project-portal"
          element={
            <PrivateRoute>
              <ProjectPortal />
            </PrivateRoute>
          }
        />
        {/* Company Portal: guard blocks restricted and site-management users */}
        <Route
          path="/company-portal"
          element={
            <PrivateRoute>
              <CompanyPortalGuard />
            </PrivateRoute>
          }
        >
          <Route index element={<CompanyPortal />} />
          <Route path="employees" element={<Employees />} />
          <Route path="company-induction" element={<CompanyInductionsList />} />
          <Route path="dashboards" element={<CompanyPortalDashboard />} />
          <Route path="projects" element={<CompanyProjects />} />
          <Route path="projects/:id" element={<CompanyPortalProjectLayout />}>
            <Route index element={<ProjectDashboard />} />
            <Route path="good-calls" element={<GoodCalls />} />
            <Route path="good-calls-dashboard" element={<GoodCallsDashboard />} />
            <Route path="close-calls-dashboard" element={<CloseCallsDashboard />} />
            <Route path="close-calls" element={<CloseCalls />} />
            <Route path="inductions" element={<Inductions />} />
            <Route path="inspections" element={<Inspections />} />
            <Route path="rams" element={<RAMS />} />
            <Route path="toolbox-talks" element={<ToolboxTalks />} />
            <Route path="task-briefing" element={<TaskBriefing />} />
            <Route path="give-task-briefing" element={<GiveTaskBriefing />} />
            <Route path="give-task-briefing/:tbId" element={<ViewTaskBriefingDocument />} />
            <Route path="give-task-briefing/:tbId/take-register" element={<TakeTaskBriefingRegister />} />
            <Route path="requisitions" element={<Requisitions />} />
            <Route path="requisitions/returns" element={<Requisitions />} />
            <Route path="asset-management" element={<AssetManagementHome />} />
            <Route path="asset-management/:category" element={<AssetManagement />} />
            <Route path="give-briefing" element={<GiveBriefing />} />
            <Route path="give-briefing/:ramsId" element={<ViewRAMSDocument />} />
            <Route path="give-briefing/:ramsId/take-register" element={<TakeRegister />} />
            <Route path="give-toolbox-briefing" element={<GiveToolboxTalkBriefing />} />
            <Route path="give-toolbox-briefing/:tbtId" element={<ViewToolboxTalkDocument />} />
            <Route path="give-toolbox-briefing/:tbtId/take-register" element={<TakeToolboxTalkRegister />} />
          </Route>
          <Route path="maps" element={<CompanyMaps />} />
        </Route>
        <Route
          path="/user-portal"
          element={
            <PrivateRoute>
              <UserPortal />
            </PrivateRoute>
          }
        />
        <Route
          path="/company-induction/:projectId"
          element={
            <PrivateRoute>
              <CompanyInductionSearchResult />
            </PrivateRoute>
          }
        />
        <Route
          path="/company-induction/:projectId/start"
          element={
            <PrivateRoute>
              <CompanyInduction />
            </PrivateRoute>
          }
        />
        <Route
          path="/health-medical-assessment-intro/:projectId"
          element={
            <PrivateRoute>
              <HealthMedicalAssessmentIntro />
            </PrivateRoute>
          }
        />
        <Route
          path="/health-medical-assessment/:projectId/start"
          element={
            <PrivateRoute>
              <HealthMedicalAssessment />
            </PrivateRoute>
          }
        />
        <Route
          path="/check/plant-and-machinery/:recordId"
          element={
            <PrivateRoute>
              <PlantAndMachineryDailyCheck />
            </PrivateRoute>
          }
        />
        <Route
          path="/check/equipment/:recordId"
          element={
            <PrivateRoute>
              <EquipmentDailyCheck />
            </PrivateRoute>
          }
        />
        <Route
          path="/check/access-equipment/:recordId"
          element={
            <PrivateRoute>
              <AccessEquipmentDailyCheck />
            </PrivateRoute>
          }
        />
        <Route
          path="/check/temporary-works/:recordId"
          element={
            <PrivateRoute>
              <TemporaryWorksDailyCheck />
            </PrivateRoute>
          }
        />
        <Route
          path="/site-induction-intro/:projectId"
          element={
            <PrivateRoute>
              <SiteInductionIntro />
            </PrivateRoute>
          }
        />
        <Route
          path="/site-induction/:projectId/start"
          element={
            <PrivateRoute>
              <SiteInduction />
            </PrivateRoute>
          }
        />
        {/* Main App Routes - With Layout */}
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }
        >
          <Route index element={<Navigate to="/portal-selection" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="system-induction" element={<SystemInduction />} />
          <Route path="health-medical-assessment/:projectId/edit" element={<HealthMedicalAssessment editMode={true} />} />
          <Route path="timesheets" element={<Timesheets />} />
          <Route path="inductions" element={<Inductions />} />
          <Route path="incidents" element={<Incidents />} />
          <Route path="deliveries" element={<Deliveries />} />
          <Route path="inspections" element={<Inspections />} />
          <Route path="assets" element={<Assets />} />
          <Route path="vehicles" element={<Vehicles />} />
          <Route path="users" element={<Navigate to="/settings?section=users" replace />} />
          <Route path="settings" element={<Settings />} />
          <Route path="my-data" element={<MyData />} />
          <Route path="projects" element={<Projects />} />
          <Route path="create-project" element={<CreateProject />} />
          <Route path="edit-project/:id" element={<EditProject />} />
        </Route>

        {/* Project Dashboard - Custom Layout with nested routes */}
        <Route
          path="/project-dashboard/:id"
          element={
            <PrivateRoute>
              <ProjectDashboardLayout />
            </PrivateRoute>
          }
        >
          <Route index element={<ProjectDashboard />} />
          <Route path="good-calls" element={<GoodCalls />} />
          <Route path="good-calls-dashboard" element={<GoodCallsDashboard />} />
          <Route path="close-calls-dashboard" element={<CloseCallsDashboard />} />
          <Route path="close-calls" element={<CloseCalls />} />
          <Route path="requisitions" element={<Requisitions />} />
          <Route path="requisitions/returns" element={<Requisitions />} />
          <Route path="requisitions/new" element={<RequisitionForm />} />
          <Route path="requisitions/:requisitionId/edit" element={<RequisitionForm />} />
          <Route path="requisitions/return/new" element={<RequisitionReturnForm />} />
          <Route path="requisitions/return/:returnId/edit" element={<RequisitionReturnForm />} />
          <Route path="asset-management" element={<AssetManagementHome />} />
          <Route path="asset-management/:category" element={<AssetManagement />} />
          <Route path="inductions" element={<Inductions />} />
          <Route path="inspections" element={<Inspections />} />
          <Route path="rams" element={<RAMS />} />
          <Route path="toolbox-talks" element={<ToolboxTalks />} />
          <Route path="task-briefing" element={<TaskBriefing />} />
          <Route path="give-task-briefing" element={<GiveTaskBriefing />} />
          <Route path="give-task-briefing/:tbId" element={<ViewTaskBriefingDocument />} />
          <Route path="give-task-briefing/:tbId/take-register" element={<TakeTaskBriefingRegister />} />
          <Route path="give-briefing" element={<GiveBriefing />} />
          <Route path="give-briefing/:ramsId" element={<ViewRAMSDocument />} />
          <Route path="give-briefing/:ramsId/take-register" element={<TakeRegister />} />
          <Route path="give-toolbox-briefing" element={<GiveToolboxTalkBriefing />} />
          <Route path="give-toolbox-briefing/:tbtId" element={<ViewToolboxTalkDocument />} />
          <Route path="give-toolbox-briefing/:tbtId/take-register" element={<TakeToolboxTalkRegister />} />
          {/* Add more tool routes here as they're built */}
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

export default App;
